
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"
import { State, Action } from "vuex-class"

import UserProfilePictureModal from "./UserProfilePictureModal.vue"

import api from "@/services/api"

import { BaseState } from "@/store"

import { User } from "@/types"
import { CurrentUser } from "@/types/user"

import Guid from "@/scripts/guid"

	@Component({
		components: {
			"user-profile-picture-modal": UserProfilePictureModal
		}
	})
export default class UserProfilePicture extends Vue {
		public $refs!: {
			pictureModal: UserProfilePictureModal
		};

		@Prop({ type: Object, default: null }) public readonly userDetails!: User;

		@State((state: BaseState) => state.me) private readonly me!: CurrentUser;
		@Action private readonly refreshUserInfo!: () => Promise<void>;

		private profilePictureSet: boolean = false;
		private editingProfilePicture: boolean = false;
		private pictureGuid: string = Guid.newGuid();

		/**
		* @summary Gets the profile picture URL for the user.
		*/
		public get userPictureUrl (): string {
			if (!this.userDetails) {
				return ""
			}

			let imageUrl = ""

			if (this.userDetails.pictureUrl) {
				imageUrl = this.userDetails.pictureUrl!
			} else {
				imageUrl = this.getUploadedPictureUrl(this.userDetails.id)
			}

			return `${imageUrl}?p=${this.pictureGuid}`
		}

		/**
		 * @summary Gets a value indicating whether the current user can change this user's profile picture (either they're an admin,
		 * or this is their own profile picture).
		 */
		public get canEditUserPicture (): boolean {
			if (!this.me || !this.userDetails) {
				return false
			}

			return this.me.isAdmin || this.me.id === this.userDetails.id
		}

		/**
		 * @summary Watcher fired when the user details changes. Checks whether the user has a custom profile picture already uploaded.
		 */
		@Watch("userDetails", { immediate: true })
		private async onUserDetailsChanged (userDetails: User | null): Promise<void> {
			if (!userDetails) {
				this.profilePictureSet = false
			} else {
				this.profilePictureSet = await api.hasUploadedPicture(userDetails.id)
			}
		}

		/**
		 * @summary Gets the uploaded picture URL for the specified user ID.
		 * @param userId The ID of the user.
		 * @returns The profile picture URL for the user.
		 */
		private getUploadedPictureUrl (userId: string): string {
			return api.pictureUrl(userId)
		}

		private openPictureSelect (): void {
			// @ts-ignore
			this.$refs.pictureModal.openPictureSelect()
		}

		/**
		 * @summary Handler for when the user deletes the profile picture.
		 */
		@Emit("picture-updated")
		private async onProfilePictureDeleted (): Promise<void> {
			await api.removeUserImage(this.userDetails.id)
			await this.onProfilePictureUpdated()
		}

		/**
		 * @summary Handler for when the users profile picture is updated, generates a new GUID to refresh the image URL.
		 */
		@Emit("picture-updated")
		private async onProfilePictureUpdated (): Promise<void> {
			// Trigger the profile picture to reload, clear any old picture URL
			this.pictureGuid = Guid.newGuid()
			this.profilePictureSet = await api.hasUploadedPicture(this.userDetails.id)

			if (this.userDetails.id === this.me.id) {
				// If the user has updated their own picture, trigger a picture refresh within the navbar.
				await this.refreshUserInfo()
				this.$root.$emit("refresh-profile-picture")
			}
		}
}
